import { MutationOptions, useMutation } from "react-query";
import { APIError, APIResponse, post ,API_URL_V3} from "../api";

/**
 * The onboarding-survey endpoint lives on a different host (api.visaro.ng.com)
 * from the main API (app.visaro.ng). Using a relative URL would route through
 * the CRA dev proxy which only targets app.visaro.ng and returns 404.
 * We always use the absolute URL here.
 */

export interface OnboardingSurveyPayload {
  /** 1–5: how easy was it to create the account */
  ease_of_account_creation: number;
  /** 1–5: how would they rate the onboarding time */
  onboarding_time_rating: number;
  /** true if the user experienced verification code delays */
  verification_code_delays: boolean;
  /** 1–5: clarity of the verification/KYC process */
  verification_process_clarity: number;
  /** Free-text description of where they got stuck (max 2000 chars, optional) */
  stuck_points?: string;
  /** 1–5: clarity of next steps after onboarding */
  next_steps_clarity: number;
  /** Free-text additional feedback (max 2000 chars, optional) */
  additional_feedback?: string;
  /** Optional contact phone number */
  contact_phone?: string;
}

export interface OnboardingSurveyResponse {
  requestId: string;
  survey_id: number;
  submitted_at: string;
}

function useSubmitOnboardingSurvey(
  options?: MutationOptions<
    APIResponse<OnboardingSurveyResponse>,
    APIError,
    OnboardingSurveyPayload
  >
) {
  const {
    isLoading: submittingSurvey,
    isError: isSurveyError,
    error: surveyError,
    mutate: submitSurvey,
    mutateAsync: submitSurveyAsync,
    reset: resetSurvey,
  } = useMutation<
    APIResponse<OnboardingSurveyResponse>,
    APIError,
    OnboardingSurveyPayload
  >(
    "submit_onboarding_survey",
    async (variables) => {
      const token = localStorage.getItem("token");
      return await post(
        `${API_URL_V3}/onboarding-survey`,
        variables,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
    },
    options
  );

  return {
    submitSurvey,
    submitSurveyAsync,
    submittingSurvey,
    isSurveyError,
    surveyError,
    resetSurvey,
  };
}

export default useSubmitOnboardingSurvey;
